import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ProductCard from '../../components/ui/ProductCard';
import { categories, allProducts } from '../../mocks/products';

const CategoriesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('featured');

  const brands = ['سامسونج', 'إل جي', 'بوش', 'وايت ويل', 'كريازي', 'توشيبا', 'شارب'];

  const toggleBrand = (brand: string) => {
    setSelectedBrands(prev =>
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
  };

  const filteredProducts = allProducts.filter(product => {
    if (selectedCategory !== 'all' && product.categoryId !== selectedCategory) return false;
    if (product.price < priceRange[0] || product.price > priceRange[1]) return false;
    if (selectedBrands.length > 0 && !selectedBrands.includes(product.brand)) return false;
    return true;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low': return a.price - b.price;
      case 'price-high': return b.price - a.price;
      case 'name': return a.name.localeCompare(b.name, 'ar');
      default: return 0;
    }
  });

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">جميع الأقسام والمنتجات</h1>
          <p className="text-xl text-white/90">اكتشف مجموعتنا الواسعة من المنتجات والحلول</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <i className="ri-filter-3-line text-[#FF8F00]"></i>
                تصفية النتائج
              </h3>

              {/* Categories Filter */}
              <div className="mb-8">
                <h4 className="font-bold text-gray-800 mb-4">الأقسام</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`w-full text-right px-4 py-3 rounded-lg transition-all cursor-pointer ${
                      selectedCategory === 'all'
                        ? 'bg-[#0D47A1] text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    جميع المنتجات
                  </button>
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-right px-4 py-3 rounded-lg transition-all cursor-pointer flex items-center gap-3 ${
                        selectedCategory === category.id
                          ? 'bg-[#0D47A1] text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`${category.icon} text-xl`}></i>
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-8">
                <h4 className="font-bold text-gray-800 mb-4">نطاق السعر</h4>
                <div className="space-y-4">
                  <input
                    type="range"
                    min="0"
                    max="50000"
                    step="500"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full accent-[#0D47A1] cursor-pointer"
                  />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">0 ريال</span>
                    <span className="font-bold text-[#0D47A1]">{priceRange[1].toLocaleString('ar-SA')} ريال</span>
                  </div>
                </div>
              </div>

              {/* Brands Filter */}
              <div className="mb-8">
                <h4 className="font-bold text-gray-800 mb-4">الماركات</h4>
                <div className="space-y-2">
                  {brands.map(brand => (
                    <label
                      key={brand}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                        className="w-5 h-5 accent-[#0D47A1] cursor-pointer"
                      />
                      <span className="text-gray-700">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Reset Filters */}
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setPriceRange([0, 50000]);
                  setSelectedBrands([]);
                }}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                إعادة تعيين الفلاتر
              </button>
            </div>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            {/* Sort and Results Count */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-gray-600">
                  <span className="font-bold text-[#0D47A1]">{sortedProducts.length}</span> منتج متاح
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-600">ترتيب حسب:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 pr-8 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0D47A1] cursor-pointer"
                  >
                    <option value="featured">الأكثر شهرة</option>
                    <option value="price-low">السعر: من الأقل للأعلى</option>
                    <option value="price-high">السعر: من الأعلى للأقل</option>
                    <option value="name">الاسم</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {sortedProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {sortedProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <ProductCard {...product} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-search-line text-5xl text-gray-400"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">لا توجد منتجات</h3>
                <p className="text-gray-600 mb-6">جرب تغيير الفلاتر للحصول على نتائج أفضل</p>
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setPriceRange([0, 50000]);
                    setSelectedBrands([]);
                  }}
                  className="bg-[#0D47A1] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#0a3a8a] transition-colors cursor-pointer whitespace-nowrap"
                >
                  إعادة تعيين الفلاتر
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CategoriesPage;